import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPaperclip,
  faPaperPlane,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { Col } from 'react-bootstrap'
import Avatar from 'design-system/avatar'
import Button from 'design-system/button'
import { ButtonVariant } from 'design-system/button/src/Button'
import { commmonAllowedFiles } from 'helpers/allowedExtensionFiles.helper'
import { awesomeFileInputValidator } from 'helpers/validators.helper'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import { truncateNameKeepExtension } from 'helpers/converters.helper'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import styles from './ChatInput.module.scss'

interface IChatInput {
  onSend: any
  userData: any
  isAdmin: boolean
}

const MIN_TEXTAREA_HEIGHT = 30
const MAX_TEXTAREA_HEIGHT = 54

const ChatInput: React.FC<IChatInput> = ({
  onSend,
  userData: { avatar, id },
  isAdmin,
}) => {
  const { t } = useTranslation()
  let inputRef: any = useRef()
  const [newMessage, setNewMessage] = useState('')
  const [files, setFilesArray] = useState([] as any)
  const textareaRef: any = useRef()

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return

    textarea.style.height = '0px'
    const nextHeight = Math.min(
      MAX_TEXTAREA_HEIGHT,
      Math.max(MIN_TEXTAREA_HEIGHT, textarea.scrollHeight)
    )
    textarea.style.height = `${nextHeight}px`
  }

  useEffect(() => {
    if (!textareaRef.current) return

    adjustTextareaHeight(textareaRef.current)
    textareaRef.current.focus()
  }, [])

  useEffect(() => {
    adjustTextareaHeight(textareaRef.current)
  }, [newMessage])

  const handleUploadFile = (e: any) => {
    if (e.target.files.length) {
      const newFiles = Object.values(e.target.files)
      const result = [] as any
      const sizes = files.reduce((acc: number, file: any) => {
        return acc + file.size
      }, 0)
      let sizeExceeded = false
      newFiles.some((file: any) => {
        const exist = files.findIndex((aux: any) => aux.name === file.name)
        if (26214400 - sizes < file.size) {
          sizeExceeded = true
        } else {
          exist >= 0
            ? ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sameName`))
            : result.push(file)
        }
      })
      sizeExceeded &&
        ErrorToast(
          t(`lessons.contentBlocks.taskBlock.toast.totalMaxSizeExceeded`)
        )
      const filesReadyToUpload = awesomeFileInputValidator(
        result,
        commmonAllowedFiles,
        26214400,
        '25 MB',
        true,
        26214400
      )

      filesReadyToUpload.length > 0 &&
        setFilesArray(files.concat(filesReadyToUpload))
    }
    inputRef.value = ''
  }

  const handleSendMessage = () => {
    if (newMessage !== '' || files.length > 0) {
      onSend({
        userId: id,
        message: newMessage,
        files,
      })
      setNewMessage('')
      setFilesArray([])
    }
  }

  const handleKeyPress = (e: any) => {
    if (!e.altKey && !e.ctrlKey && !e.shiftKey && e.keyCode == 13) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleDeleteFile = (index: number) => {
    const newArray = files.slice()
    newArray.splice(index, 1)
    setFilesArray(newArray)
  }

  return (
    <>
      {files.length > 0 && (
        <div className="mb-2 d-block">
          <div className={styles.filesContainer}>
            {files.map((file: any, index: number) => (
              <div key={file.name + index} className={styles.file}>
                <FontAwesomeIcon icon={faPaperclip}></FontAwesomeIcon>
                <span className="ml-1 mr-2">
                  {truncateNameKeepExtension(file.name)}
                </span>
                <Button
                  className={`${styles.iconButton} close ml-1`}
                  variant={ButtonVariant.Secondary}
                  onClick={() => handleDeleteFile(index)}
                >
                  <span>
                    <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                  </span>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div
        className={`${styles.input} w-100 ${newMessage ? styles.active : ''}`}
      >
        <input
          id={`contained-button-file`}
          ref={(ref) => (inputRef = ref)}
          multiple={true}
          className="file-input d-none"
          type="file"
          onChange={handleUploadFile}
        />
        <div className={styles.avatarSlot}>
          <Avatar src={avatar || undefined} className={styles.noMargin} />
        </div>
        <Col className={`${styles.textareaCol} px-2`}>
          <textarea
            ref={textareaRef}
            rows={1}
            id="text-area"
            onKeyDown={handleKeyPress}
            value={newMessage}
            placeholder={t('components.chat.writeAMessage')}
            className={`w-100 ${styles.messageInput}`}
            onChange={(e: any) => setNewMessage(e.target.value)}
          />
        </Col>
        <div className={styles.actionsSlot}>
          <CommonToolTip
            text={t('components.chat.tooltips.sizeLimit')}
            position="top"
            className={styles.iconButtonContainer}
          >
            <div>
              <Button
                className={styles.iconButton}
                variant={ButtonVariant.Secondary}
                onClick={() => inputRef.click()}
              >
                <span>
                  <FontAwesomeIcon icon={faPaperclip}></FontAwesomeIcon>
                </span>
              </Button>
            </div>
          </CommonToolTip>
          <CommonToolTip
            text={t(
              `components.chat.tooltips.${
                isAdmin ? 'sendToUser' : 'sendToAdmin'
              }`
            )}
            position="top"
            className={styles.iconButtonContainer}
          >
            <div>
              <Button
                className={styles.iconButton}
                variant={ButtonVariant.Secondary}
                onClick={handleSendMessage}
              >
                <span>
                  <FontAwesomeIcon icon={faPaperPlane}></FontAwesomeIcon>
                </span>
              </Button>
            </div>
          </CommonToolTip>
        </div>
      </div>
    </>
  )
}

export default ChatInput
